import type {
  ChartTypeRegistry,
  ScriptableContext,
  TooltipItem
} from 'chart.js'
import { desaturate, lighten, transparentize } from 'polished'

import { formatMoney, formatMoneyV2 } from '~/helpers'
import i18n from '~/i18n'

type ChartMarkerData = {
  date?: string[]
}

type MaxValueData = Partial<
  Record<
    | 'viewNumber'
    | 'impressionsNumber'
    | 'clicksNumber'
    | 'conversionNumber'
    | 'totalSpentNumber'
    | 'incomeNumber',
    number[]
  >
>

/** Métrica que saiu do escopo não pode continuar desenhada; a posição fica vazia. */
export const dropExcludedMetrics = <T extends string>(
  selected: Record<number, T | null>,
  excludeMetrics?: T[]
): Record<number, T | null> => {
  if (!excludeMetrics?.length) return selected

  const hasExcluded = Object.values(selected ?? {}).some(
    value => !!value && excludeMetrics.includes(value)
  )

  if (!hasExcluded) return selected

  return Object.fromEntries(
    Object.entries(selected).map(([index, value]) => [
      index,
      value && excludeMetrics.includes(value) ? null : value
    ])
  )
}

const getMarkerIndex = ({
  data,
  label
}: {
  data?: ChartMarkerData | null
  label: string
}) => {
  return data?.date?.indexOf(label) ?? -1
}

export const getChartMarkerIndex = ({
  data,
  compareData,
  isCompare = false,
  marker
}: {
  data?: ChartMarkerData | null
  compareData?: ChartMarkerData | null
  isCompare?: boolean
  marker?: Pick<ChartMarker, 'label'> | null
}) => {
  if (!marker) return -1

  const markerIndex = getMarkerIndex({ data, label: marker.label })

  if (markerIndex >= 0 || !isCompare) return markerIndex

  return getMarkerIndex({ data: compareData, label: marker.label })
}

//
export const maxValue = ({ data }: { data?: MaxValueData | null }) => {
  const {
    viewNumber = [],
    impressionsNumber = [],
    clicksNumber = [],
    conversionNumber = [],
    totalSpentNumber = [],
    incomeNumber = []
  } = data || {}

  const allValues = [
    ...clicksNumber,
    ...conversionNumber,
    ...viewNumber,
    ...impressionsNumber
  ]
  const allMoneyValues = [...totalSpentNumber, ...incomeNumber]

  return {
    metrics: Math.max(...allValues),
    clicksAndConversions: Math.max(...[...clicksNumber, ...conversionNumber]),
    conversions: Math.max(...conversionNumber),
    money: Math.max(...allMoneyValues)
  }
}

const insertAbbr = (formattedValue: string, abbr: string): string => {
  const lastNumberIndex = formattedValue
    .split('')
    .reverse()
    .findIndex(char => /[0-9]/.test(char))
  if (lastNumberIndex === -1) return formattedValue

  const numberPart = formattedValue
    .slice(0, formattedValue.length - lastNumberIndex)
    .trim()

  const restPart = formattedValue
    .slice(formattedValue.length - lastNumberIndex)
    .trim()

  return `${numberPart}${abbr} ${restPart}`
}

export const abbreviateCurrency = (value: number) => {
  if (value >= 1000000) {
    return insertAbbr(formatMoney(Math.floor(value / 1000000)), 'M')
  } else if (value >= 1000) {
    return insertAbbr(formatMoney(Math.floor(value / 1000)), 'K')
  } else {
    return formatMoney(value)
  }
}

//

type FormatChartStyleProps = {
  color: string
  bgStyle?: 'solid' | 'alphaGradient'
  isCompare?: boolean
}

const getBgColor = ({ bgStyle, color }: FormatChartStyleProps) => {
  if (!color || typeof color !== 'string') {
    console.warn('getBgColor: color deve ser uma string válida', { color })
    color = '#0366DD'
  }
  const formatter = {
    solid: color,
    alphaGradient: (context: ScriptableContext<'line'>) => {
      const ctx = context.chart.ctx
      const gradient = ctx.createLinearGradient(0, 0, 0, 280)
      gradient.addColorStop(0, transparentize(0.65, color))
      gradient.addColorStop(1, transparentize(0.85, color))
      return gradient
    }
  }

  return formatter?.[bgStyle] || color
}

export const formatStylesChart = ({
  color,
  bgStyle = 'solid',
  isCompare = false
}: FormatChartStyleProps) => {
  // Validação para garantir que a cor seja uma string válida
  if (!color || typeof color !== 'string') {
    console.warn('formatStylesChart: color deve ser uma string válida', {
      color
    })
    color = '#0366DD'
  }

  const isValidColor =
    /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color) ||
    /^rgb\(/.test(color) ||
    /^rgba\(/.test(color) ||
    /^hsl\(/.test(color) ||
    /^hsla\(/.test(color)

  if (!isValidColor) {
    console.warn('formatStylesChart: cor não está em formato válido', { color })
    color = '#0366DD'
  }

  const baseColor = isCompare ? desaturate(0.25, color) : color

  return {
    backgroundColor: isCompare
      ? getBgColor({ color: baseColor, bgStyle })
      : getBgColor({ color, bgStyle }),
    borderColor: baseColor,
    borderWidth: isCompare ? 1 : 2,
    pointBorderColor: color,
    pointHoverBorderColor: color,
    pointBorderWidth: isCompare ? 1 : 2,
    pointBackgroundColor: lighten(0.3, baseColor),
    pointHoverBackgroundColor: lighten(0.2, baseColor),
    cubicInterpolationMode: 'monotone',
    pointStyle: 'rectRounded',
    pointRadius: 6,
    pointHoverRadius: 8,

    borderRadius: 8,
    maxBarThickness: 72,

    borderDash: isCompare ? [6, 6] : undefined
  }
}

export const formatCompareMetricLabel = ({
  label,
  isCompare = false
}: {
  label: string
  isCompare?: boolean
}) =>
  isCompare
    ? `${label} (${i18n.t('rm:chart.compareDatePicker.metric.suffix')})`
    : label

/**
 *
 */
type TooltipItemContext = TooltipItem<keyof ChartTypeRegistry>
export const handleLabelCallbackTooltip = (context: TooltipItemContext) => {
  let label = context.dataset.label || ''

  const id = context.dataset.yAxisID
  const moneyAxis = ['y-income', 'y-totalSpent']
  const isMoney = moneyAxis.includes(id)

  if (label) {
    label += ': '
  }

  if (isMoney && context.parsed.y !== null) {
    label += formatMoneyV2({ value: context.parsed.y })
  } else {
    label += context?.formattedValue || context.parsed.y
  }

  return label
}

/**
 *
 */
export const handleItemSortTooltip = (
  a: TooltipItemContext,
  b: TooltipItemContext
) => {
  const order = {
    a: a.dataset.order,
    b: b.dataset.order
  }

  if (order.a < order.b) {
    return -1
  }

  if (order.a > order.b) {
    return 1
  }

  return 0
}
